import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScopeLock {
    locked: boolean;
    acquire: (token: number) => boolean;
    release: (token: number) => void;
}

export interface ApprovalScope {
    token: number;
    reset: () => void;
    isCurrent: (token: number, revision?: string) => boolean;
    lock: ScopeLock;
}

interface ScopeState {
    key: string;
    token: number;
}

export const approvalScopeKey = (agreementId: string | undefined, purpose: string): string =>
    `${agreementId ?? ''}|${purpose}`;

/** One request at a time per scope token; a token that is no longer current can neither take nor
 *  release the lock that now belongs to the current scope. */
function useScopeLock(token: number, isCurrent: (token: number) => boolean): ScopeLock {
    const held = useRef<number | null>(null);
    const [heldToken, setHeldToken] = useState<number | null>(null);

    const acquire = (requested: number): boolean => {
        if (!isCurrent(requested) || held.current === requested) return false;
        held.current = requested;
        setHeldToken(requested);
        return true;
    };

    const release = (requested: number): void => {
        if (held.current === requested) held.current = null;
        setHeldToken((previous) => (previous === requested ? null : previous));
    };

    return { locked: heldToken === token, acquire, release };
}

/** A scope is one key plus explicit resets; each key change or reset() issues a new token,
 *  and the optional revision detects an input change within the same scope. */
export function useApprovalScope(key: string, revision = ''): ApprovalScope {
    const [state, setState] = useState<ScopeState>({ key, token: 0 });
    let current = state;
    if (state.key !== key) {
        current = { key, token: state.token + 1 };
        setState(current);
    }
    const token = current.token;

    const live = useRef({ token, revision });
    useEffect(() => {
        live.current = { token, revision };
    }, [token, revision]);

    const isCurrent = useCallback(
        (candidate: number, candidateRevision?: string): boolean =>
            live.current.token === candidate &&
            (candidateRevision === undefined || live.current.revision === candidateRevision),
        [],
    );

    const reset = useCallback(() => setState((previous) => ({ ...previous, token: previous.token + 1 })), []);

    const lock = useScopeLock(token, isCurrent);

    return { token, reset, isCurrent, lock };
}
